import React, { memo, useCallback, useEffect, useRef, useState, lazy, Suspense } from 'react'
import { useUser, useAuthStore } from '../store'
import { useAvatarUrl } from '../hooks/useAvatarUrl'
import { useTokenRefresh } from '../hooks'
import { UserProfileEditModal } from './UserProfileEditModal'

// 懒加载数据导入/导出弹窗：连带 antd Modal/Tabs 等重型组件移出首屏 bundle
const DataImportExport = lazy(() => import('./DataImportExport'))
// 懒加载更新弹窗：连带 antd 组件移出首屏 bundle
const UpdateModal = lazy(() => import('@components/UpdateModal/UpdateModal'))
import { useUpdateChecker } from '../../hooks/useUpdateChecker'
import { getAppVersion } from '../../services/UpdateService'
import { isTauriMobilePlatform } from '../../utils/mobile'
import { triggerFullDataResync } from '../../store/collections'
import { runReconciliation } from '../../services/cacheReconciliation'
import type { ReconciliationResult } from '../../services/cacheReconciliation'
import { ConfirmDialog } from '@components/common/ConfirmDialog'
import './UserProfile.css'

function getInitial(name?: string | null): string {
  if (!name) return '?'
  return name.charAt(0).toUpperCase()
}

function LocalAvatarImg({
  src,
  alt,
  className,
  onErrorRender,
}: {
  src: string | undefined | null
  alt: string
  className: string
  onErrorRender: () => React.ReactNode
}) {
  const [loadError, setLoadError] = useState(false)

  useEffect(() => {
    setLoadError(false)
  }, [src])

  if (loadError || !src) {
    return <>{onErrorRender()}</>
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => setLoadError(true)}
    />
  )
}

export const UserProfile = memo(function UserProfile() {
  const user = useUser()
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [importExportOpen, setImportExportOpen] = useState(false)
  const [updateModalOpen, setUpdateModalOpen] = useState(false)
  const appVersion = getAppVersion()
  const isMobile = isTauriMobilePlatform()

  // 版本与更新：手动检查模式，不自动检查（避免与 App.tsx 的启动自动检查重复）
  const {
    updateInfo,
    isChecking,
    isUpdating,
    progress,
    downloadStatus,
    error: updateError,
    checkNow,
    dismiss,
    applyUpdate,
    startDownload,
    installDownloaded,
  } = useUpdateChecker({
    checkOnMount: false,
    skipIfCheckedRecently: false,
  })

  const { avatarSrc } = useAvatarUrl(user?.name, user?.avatar)
  useTokenRefresh()
  const logout = useAuthStore((state) => state.logout)

  useEffect(() => {
    if (!isAuthenticated || user === null) {
      console.log('[UserProfile] state: isAuthenticated=false or user=null')
    } else {
      console.log('[UserProfile] user loaded:', {
        name: user.name,
        email: user.email,
        avatar: user.avatar?.substring(0, 50),
      })
    }
  }, [isAuthenticated, user])

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        dropdownOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [dropdownOpen])

  const toggleDropdown = useCallback(() => {
    setDropdownOpen((prev) => !prev)
  }, [])

  const handleEditProfile = useCallback(async () => {
    setDropdownOpen(false)
    setEditModalOpen(true)
  }, [])

  const handleEditModalClose = useCallback(() => {
    setEditModalOpen(false)
  }, [])

  const handleLogout = useCallback(() => {
    setDropdownOpen(false)
    logout()
  }, [logout])

  const handleImportExport = useCallback(() => {
    setDropdownOpen(false)
    setImportExportOpen(true)
  }, [])

  const handleImportExportClose = useCallback(() => {
    setImportExportOpen(false)
  }, [])

  const handleCheckUpdate = useCallback(async () => {
    setDropdownOpen(false)
    setUpdateModalOpen(true)
    // 打开弹窗时立即触发检查，不等待（跳过与启动自动检查的 24 小时限制）
    await checkNow()
  }, [checkNow])

  // 关闭 Modal：仅关闭 UI，不调用 dismiss（保持版本可再次被检测到）
  // "稍后提醒" 按钮会单独调用 dismiss() 标记为忽略
  const handleUpdateModalClose = useCallback(() => {
    setUpdateModalOpen(false)
  }, [])

  // 全量下载（等同冷启动）：确认后清空本地缓存、自动重载应用，启动时从云端全量重新下载
  const [fullResyncDialogOpen, setFullResyncDialogOpen] = useState(false)
  const [fullResyncLoading, setFullResyncLoading] = useState(false)
  const [fullResyncError, setFullResyncError] = useState('')

  const openFullResyncDialog = useCallback(() => {
    setFullResyncError('')
    setDropdownOpen(false)
    setFullResyncDialogOpen(true)
  }, [])

  const closeFullResyncDialog = useCallback(() => {
    if (fullResyncLoading) return
    setFullResyncDialogOpen(false)
  }, [fullResyncLoading])

  const executeFullResync = useCallback(async () => {
    if (fullResyncLoading) return
    setFullResyncLoading(true)
    setFullResyncError('')
    try {
      console.log('[UserProfile] 用户触发全量下载')
      await triggerFullDataResync('用户手动触发')
      // 触发成功后应用已重载（triggerFullDataResync 内部调用 window.location.reload()）
      setFullResyncDialogOpen(false)
    } catch (error) {
      console.error('[UserProfile] 全量下载失败:', error)
      setFullResyncError('全量下载失败，请检查网络后重试。')
    } finally {
      setFullResyncLoading(false)
    }
  }, [fullResyncLoading])

  // 缓存完整性检查：运行一次本地 ↔ 云端对账并展示结果（科学方法：可观测、可复现）
  const [cacheCheckOpen, setCacheCheckOpen] = useState(false)
  const [cacheCheckLoading, setCacheCheckLoading] = useState(false)
  const [cacheCheckResult, setCacheCheckResult] = useState<ReconciliationResult | null>(null)
  const [cacheCheckError, setCacheCheckError] = useState('')

  const openCacheCheck = useCallback(() => {
    setCacheCheckError('')
    setCacheCheckResult(null)
    setDropdownOpen(false)
    setCacheCheckOpen(true)
    void executeCacheCheck()
  }, [])

  const executeCacheCheck = useCallback(async () => {
    if (cacheCheckLoading) return
    setCacheCheckLoading(true)
    setCacheCheckError('')
    try {
      const result = await runReconciliation(user?.id || '')
      setCacheCheckResult(result)
    } catch (error) {
      console.error('[UserProfile] 缓存完整性检查失败:', error)
      setCacheCheckError('检查失败，请稍后重试')
    } finally {
      setCacheCheckLoading(false)
    }
  }, [cacheCheckLoading, user?.id])

  const closeCacheCheck = useCallback(() => {
    if (cacheCheckLoading) return
    setCacheCheckOpen(false)
  }, [cacheCheckLoading])

  if (!user) {
    console.log('[UserProfile] user is null, returning null')
    return null
  }

  const displayAvatar = avatarSrc

  return (
    <div className="user-profile" ref={dropdownRef}>
      <button
        ref={triggerRef}
        className="user-profile__avatar-button"
        onClick={toggleDropdown}
        aria-label="用户菜单"
        type="button"
      >
        {displayAvatar ? (
          <LocalAvatarImg
            src={displayAvatar}
            alt={user.name || user.email || ''}
            className="user-profile__avatar-image"
            onErrorRender={() => (
              <div className="user-profile__avatar-placeholder">{getInitial(user.name)}</div>
            )}
          />
        ) : (
          <div className="user-profile__avatar-placeholder">{getInitial(user.name)}</div>
        )}
      </button>

      {dropdownOpen && (
        <div className="user-profile__dropdown">
          <button
            type="button"
            className="user-profile__header"
            onClick={handleEditProfile}
          >
            {displayAvatar ? (
              <LocalAvatarImg
                src={displayAvatar}
                alt={user.name || user.email || ''}
                className="user-profile__header-avatar"
                onErrorRender={() => (
                  <div className="user-profile__header-avatar-placeholder">
                    {getInitial(user.name)}
                  </div>
                )}
              />
            ) : (
              <div className="user-profile__header-avatar-placeholder">
                {getInitial(user.name)}
              </div>
            )}
            <div className="user-profile__header-info">
              <div className="user-profile__header-name">{user.nickname || user.name || '未命名用户'}</div>
              <div className="user-profile__header-email">
                {user.email || '无邮箱'}
              </div>
            </div>
          </button>

          <div className="user-profile__divider" />

          <div className="user-profile__menu">
            <button
              type="button"
              className="user-profile__menu-item"
              onClick={handleEditProfile}
            >
              <svg
                className="user-profile__menu-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>我的帐号</span>
            </button>

            {!isMobile && (
              <button
                type="button"
                className="user-profile__menu-item"
                onClick={handleImportExport}
              >
                <svg
                  className="user-profile__menu-icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
                <span>数据导入/导出</span>
              </button>
            )}

            <button
              type="button"
              className="user-profile__menu-item"
              onClick={openFullResyncDialog}
            >
              <svg
                className="user-profile__menu-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 17l3 3m0 0l3-3m-3 3V12"
                />
              </svg>
              <span>全量下载</span>
            </button>

            <button
              type="button"
              className="user-profile__menu-item"
              onClick={openCacheCheck}
            >
              <svg
                className="user-profile__menu-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
              </svg>
              <span>缓存完整性检查</span>
            </button>

            <button
              type="button"
              className="user-profile__menu-item"
              onClick={handleCheckUpdate}
            >
              <svg
                className="user-profile__menu-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              <span>版本与更新</span>
              <span className="user-profile__menu-version">v{appVersion}</span>
            </button>

            <button
              type="button"
              className="user-profile__menu-item user-profile__menu-item--danger"
              onClick={handleLogout}
            >
              <svg
                className="user-profile__menu-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              <span>退出登录</span>
            </button>
          </div>
        </div>
      )}

      {editModalOpen && (
        <UserProfileEditModal
          onClose={handleEditModalClose}
        />
      )}

      {importExportOpen && (
        <Suspense fallback={null}>
          <DataImportExport
            open={importExportOpen}
            onClose={handleImportExportClose}
          />
        </Suspense>
      )}

      {updateModalOpen && (
        <Suspense fallback={null}>
          <UpdateModal
            visible={updateModalOpen}
            updateInfo={updateInfo}
            isUpdating={isUpdating}
            isChecking={isChecking}
            progress={progress}
            error={updateError}
            downloadStatus={downloadStatus}
            onClose={handleUpdateModalClose}
            onUpdate={applyUpdate}
            onCheckNow={checkNow}
            onDismiss={dismiss}
            onStartDownload={startDownload}
            onInstallDownloaded={installDownloaded}
            onRetryDownload={startDownload}
          />
        </Suspense>
      )}

      <ConfirmDialog
        open={fullResyncDialogOpen}
        title="全量下载"
        content={
          <>
            <p style={{ margin: 0 }}>
              全量下载将清空本地缓存并从云端重新下载全部数据（笔记与块引用，等同冷启动），应用会自动重载，期间显示"正在全量下载云端数据…"，确定继续吗？
            </p>
            {fullResyncError && (
              <p style={{ margin: '8px 0 0', color: '#dc2626', fontSize: '13px' }}>
                {fullResyncError}
              </p>
            )}
          </>
        }
        confirmText="开始全量下载"
        cancelText="取消"
        danger
        loading={fullResyncLoading}
        onConfirm={executeFullResync}
        onCancel={closeFullResyncDialog}
      />

      {/* 缓存完整性检查结果 */}
      <ConfirmDialog
        open={cacheCheckOpen}
        title="缓存完整性检查"
        content={
          cacheCheckLoading ? (
            <p style={{ margin: 0 }}>正在核对本地缓存与云端数据，请稍候…</p>
          ) : cacheCheckError ? (
            <p style={{ margin: 0, color: '#dc2626' }}>{cacheCheckError}</p>
          ) : !cacheCheckResult ? (
            <p style={{ margin: 0 }}>云快照未确认（可能正在同步或离线），暂时无法对账，请稍后重试。</p>
          ) : (
            <>
              <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.7 }}>
                云端 {cacheCheckResult.cloudCount} 条 · 本地 {cacheCheckResult.localCount} 条
              </p>
              <p style={{ margin: '6px 0 0', fontSize: '13px', color: cacheCheckResult.missing.length + cacheCheckResult.stale.length > 0 ? '#dc2626' : '#16a34a' }}>
                {cacheCheckResult.missing.length === 0 && cacheCheckResult.stale.length === 0 && cacheCheckResult.extra.length === 0
                  ? '✓ 本地缓存与云端一致'
                  : cacheCheckResult.repaired
                    ? `已自动修复：${cacheCheckResult.repairReason}（应用将重载以完成恢复）`
                    : cacheCheckResult.repairReason
                      ? `未修复：${cacheCheckResult.repairReason}`
                      : `发现差异：丢失 ${cacheCheckResult.missing.length} 条、过期 ${cacheCheckResult.stale.length} 条、本地多余 ${cacheCheckResult.extra.length} 条（云端清单可能未同步完成）`}
              </p>
              {cacheCheckResult.repairReason && !cacheCheckResult.repaired && (
                <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#6b7280' }}>
                  提示：若确认本地数据已丢失，可在菜单中手动执行"全量下载"。
                </p>
              )}
            </>
          )
        }
        confirmText="完成"
        cancelText={cacheCheckLoading ? undefined : '重新检查'}
        danger={false}
        loading={cacheCheckLoading}
        onConfirm={closeCacheCheck}
        onCancel={() => {
          if (cacheCheckLoading) return
          void executeCacheCheck()
        }}
      />
    </div>
  )
})

export default UserProfile
